
import { forwardRef, type JSX } from "react";
import { DeploymentRequirement, RiskPlusReport, RiskProfilePillar } from "../../entities/report/RiskPlusReport";
import type { MitigationRequirement, RiskRegistryItem } from "../../entities/report/RiskRegistry";
import InternalRiskRadarChart from "./InternalRiskRadarChart";
import ExternalRiskRadarChart from "./ExternalRiskRadarChart";
import { toRankIdx, type TRiskRank, type TRiskRankWithNA } from "../../entities/risk/Risk";
import { harmCategoryToLabel } from "../../entities/risk/Harm";
import { useReportPagination } from "@/hooks/useReportPagination";
import { useTocPageNumbers } from "@/hooks/useTocPageNumbers";

import reportCoverBg from "../../assets/report-cover-bg.png";
import aiqurisCoverLogo from "../../assets/AIQURIS Logo Horizontal White and Light Blue (1).png";


import aiqurisLogo from "../../assets/Aiquris Logo.png";

const ReportFooterMainPage = () => (
  <div className="report-footer report-footer-main-page">
    <div className="footer-left"></div>
    <div className="footer-center">© {new Date().getFullYear()} AIQURIS. Confidential and Proprietary.</div>
    <div className="footer-right">
      <img src={aiqurisCoverLogo} alt="AIQURIS" className="footer-logo footer-logo-cover p-1" />
    </div>
  </div>
);
const ReportFooter = () => (
  <div className="report-footer">
    <div className="footer-left"></div>
    <div className="footer-center">© {new Date().getFullYear()} AIQURIS. Confidential and Proprietary.</div>
    <div className="footer-right">
      <img src={aiqurisLogo} alt="AIQURIS" className="footer-logo" />
    </div>
  </div>
);

// eslint-disable-next-line react-refresh/only-export-components
export const REPORT_COVER_TEXT = {
  defaultTitle: "AIQURIS Risk+ Report",
  labelAiSystemAssessed: "AI System Assessed:",
  labelOrganisationName: "Organisation:",
  labelAssessmentDate: "Assessment Date:",
};

const renderTitlePage = (reportData: RiskPlusReport) => {

  return (
    <header
      className="container header report-cover"
      style={{ backgroundImage: `url(${reportCoverBg})` }}
    >
      <div className="report-cover-inner">
        <div className="report-cover-title-block">
          <h1 className="print-title report-cover-title">
            {reportData.titlePage.title || REPORT_COVER_TEXT.defaultTitle}
          </h1>
        </div>
        <div className="meta report-cover-meta">
          <div className="report-cover-meta-row">
            <span className="report-cover-meta-key">{REPORT_COVER_TEXT.labelAiSystemAssessed}</span>
            <span className="report-cover-meta-value">{reportData.titlePage.useCaseTitle && reportData.titlePage.useCaseTitle !== "N/A" ? reportData.titlePage.useCaseTitle : "-"}</span>
          </div>
          <div className="report-cover-meta-row">
            <span className="report-cover-meta-key">{REPORT_COVER_TEXT.labelOrganisationName}</span>
            <span className="report-cover-meta-value">{reportData.titlePage.client && reportData.titlePage.client !== "N/A" ? reportData.titlePage.client : "-"}</span>
          </div>
          <div className="report-cover-meta-row">
            <span className="report-cover-meta-key">{REPORT_COVER_TEXT.labelAssessmentDate}</span>
            <span className="report-cover-meta-value">{reportData.titlePage.reportDate ? new Date(reportData.titlePage.reportDate).toLocaleDateString() : "-"}</span>
          </div>
        </div>
      </div>
      <ReportFooterMainPage />
    </header>
  )
}

const DISCLAIMER_TEXT = {
  heading: "10. Disclaimer",
  paragraphs: [
    "This Report has been prepared by AIQURIS Pte. Ltd. solely for the use of the Client identified herein and for the specificpurpose described in this Report. It may not be relied upon by any other party without prior written consent from AIQURIS.",
    "The Client may share this Report at its discretion, including internally and with external parties such as regulators, auditors, legal counsel, and insurers. However, the Client must not publicly disclose or publish this Report, in whole or in part, without the prior written consent of AIQURIS. Where this Report is shared with third parties, it must be shared in its entirety and without modification, so that all limitations, disclaimers, and context are preserved. This Report must not be represented as a certification, formal assurance, or regulatory approval.",
    "This Report is generated automatically based solely on information entered by the Client through the AIQURIS guided assessment interface. Such information may include material sourced by the Client from third parties. AIQURIS has not independently verified any information provided, whether originating from the Client or third-party sources, and has had no direct engagement with any such third parties. The accuracy and completeness of this Report is therefore directly dependent on the quality and completeness of the inputs provided by the Client.",
    "This Report represents a point-in-time assessment conducted under the defined scope and methodology. AI systems, including large language models and retrieval-augmented systems, exhibit inherent variability, may change behaviour following vendor updates, and present assessment challenges that are not fully addressed by any current methodology. The findings in this Report reflect the state of the system and available assessment techniques at the time of engagement and should not be interpreted as a guarantee of completeness or as a definitive characterisation of all risks present. AI systems, models, controls and regulatory requirements may evolve, and no obligation is assumed to update this Report unless formally engaged to do so.",
    "All risk ratings and findings contained in this Report represent inherent risk levels assessed prior to the implementation or verification of any mitigating controls. They do not represent residual risk and must not be relied upon as confirmation that risks are managed, controlled, or acceptable for deployment purposes.",
    "This Report does not constitute legal advice, regulatory approval or certification unless explicitly stated. Responsibility for compliance with applicable laws and regulations remains with the Client. All intellectual property rights in the methodology, framework, scoring logic, and structure underlying this Report remain the property of AIQURIS regardless of payment. Payment grants the Client a non-exclusive, non-transferable right to use this Report for its own risk management and governance purposes.",
    "To the fullest extent permitted by law, AIQURIS shall not be liable for any indirect, consequential or incidental damages arising from reliance on this Report. Any direct liability of AIQURIS arising out of or in connection with this Report shall be limited to the fees paid by the Client for the specific engagement to which this Report relates."
  ],
};

type TocEntry = { num: string; title: string; sectionId: string; sub: boolean; page: number };

const getTocEntries = (reportData: RiskPlusReport): TocEntry[] => {
  const entries: TocEntry[] = [];
  let mainNum = 1;
  let page = 3; // page 1 = cover, page 2-3 = risk snapshot/scope note + executive interpretation, page 4 = TOC itself

  // TOC itself
  page++;

  // Executive Summary (renderExecutiveSummary)
  page++;
  const execNum = mainNum++;
  entries.push({ num: String(execNum), title: "Executive Summary", sectionId: "toc-executive-summary", sub: false, page });

  // How to Read This Report (renderHowToReadReport)
  page++;
  entries.push({ num: String(mainNum++), title: "How to Read This Report", sectionId: "toc-how-to-read", sub: false, page });

  // Risk Methodology (renderRiskMethodology)
  page++;
  const methNum = mainNum++;
  entries.push({ num: String(methNum), title: "Risk Methodology", sectionId: "toc-methodology", sub: false, page });

  // Risk Domain Profile (renderRiskProfile)
  page++;
  const profileNum = mainNum++;
  entries.push({ num: String(profileNum), title: "Risk Domain Profile", sectionId: "toc-risk-domain-profile", sub: false, page });
  // reportData.riskProfileSummary.pillars.forEach((pillar, idx) => {
  //   entries.push({ num: `${profileNum}.${idx + 1}`, title: pillar.label, sectionId: "toc-risk-domain-profile", sub: true, page });
  // });

  // Deployment Risk Profile (renderDeploymentProfile)
  page++;
  const deployNum = mainNum++;
  entries.push({ num: String(deployNum), title: "Deployment Risk Profile", sectionId: "toc-deployment-profile", sub: false, page });

  // Risk Concentration (part of renderDeploymentProfile, same page)
  const concentrationNum = mainNum++;
  entries.push({ num: String(concentrationNum), title: "Risk Concentration", sectionId: "toc-risk-concentration", sub: false, page });

  // Section 7: Detailed Risk Findings (heading + intro on same page as 7.1), then 7.1, 7.2, ...
  page++;
  entries.push({ num: "7", title: "Detailed Risk Findings", sectionId: "toc-detailed-risk-findings", sub: false, page });
  reportData.riskRegister.forEach((risk, idx) => {
    if (idx > 0) page++;
    const safeId = `toc-risk-${String(risk.id).replace(/[^a-zA-Z0-9-_]/g, "-")}`;
    entries.push({ num: `7.${idx + 1}`, title: risk.label, sectionId: safeId, sub: true, page });
  });
  mainNum++; // next main section is 8

  // Top Requirements (renderTopRequirements) - section 8
  page++;
  entries.push({ num: "8", title: "Top Required Controls (Not Verified) ", sectionId: "toc-top-requirements", sub: false, page });
  mainNum++;

  // From Risk Assessment to Risk Control and Assurance (renderAssurancePage) - section 9
  page++;
  entries.push({ num: "9", title: "From Risk Assessment to Risk Control and Assurance", sectionId: "toc-assurance", sub: false, page });
  mainNum++;

  // Disclaimer (renderDisclaimer) - final page
  page++;
  entries.push({ num: "10", title: "Disclaimer", sectionId: "toc-disclaimer", sub: false, page });

  return entries;
};

const renderTableOfContents = (reportData: RiskPlusReport) => {
  const tocEntries = getTocEntries(reportData);
  return (
    <section className="report-page report-toc-page">
      <h1 className="report-toc-title">Table of Contents</h1>
      <ul className="report-toc-list">
        {tocEntries.map((entry, idx) => (
          <li key={`${entry.num}-${idx}`} className={entry.sub ? "report-toc-entry report-toc-entry-sub" : "report-toc-entry"}>
            <span className="report-toc-label">
              <strong>{entry.num}</strong> {entry.title}
            </span>
            <span className="report-toc-dots" aria-hidden="true" />
            <span className="report-toc-page" data-toc-page-for={entry.sectionId}>{entry.page}</span>
          </li>
        ))}
      </ul>
      <ReportFooter />
    </section>
  );
};

const renderDisclaimer = () => {
  return (
    <section id="toc-disclaimer" className="report-page report-disclaimer-page">
      <div className="report-disclaimer">
        <h2 className="report-disclaimer-title">{DISCLAIMER_TEXT.heading}</h2>
        {DISCLAIMER_TEXT.paragraphs.map((para, index) => (
          <p key={index} className="report-disclaimer-para">{para}</p>
        ))}
      </div>
      <ReportFooter />
    </section>
  );
};

const formatRiskRank = (rank: string) =>
  rank && rank.length > 0 ? rank.charAt(0).toUpperCase() + rank.slice(1).toLowerCase() : rank;

const renderDeploymentVerdict = (reportData: RiskPlusReport) => {
  const verdict = reportData.executiveReadinessVerdict;
  const impactRank = verdict.overallRiskRank;

  return (
    <>
    <section id="toc-risk-snapshot" className="deployment-card report-page">
      <div className="risk-snapshot-block">
        <h1 className="risk-snapshot-title">{verdict.overallVerdict || ""}</h1>
        <div className="risk-snapshot-rows">
          <p className="risk-snapshot-row">
            <span className="risk-snapshot-label">Institutional Impact Classification : </span>
            <span className="risk-snapshot-value">
              <span
                className={`risk-snapshot-indicator risk-snapshot-indicator--${String(impactRank).toLowerCase()}`}
                aria-hidden="true"
              />
              {formatRiskRank(impactRank)}
            </span>
          </p>
          <p className="risk-snapshot-row">
            <span className="risk-snapshot-label">Assessment Stage: </span>
            <span className="risk-snapshot-value">Pre-Mitigation</span>
          </p>
        </div>
      </div>

      <div className="section">
        <h3>Risk Domain Status</h3>
        <table className="risk-table">
          <thead>
            <tr>
              <th>Risk Domain</th>
              <th>Status</th>
              <th>Why it matters</th>
            </tr>
          </thead>
          <tbody>
            {reportData.executiveReadinessVerdict.useCaseImpacts.map((impact, index) => (
              <tr key={index}>
                <td>{impact.impactArea}</td>
                <td className='flex gap-2 justify-center items-center'>
                  <span className={`badge ${impact.riskRating.toLowerCase()}`}></span>
                  <span>{impact.riskRating.slice(0, 1).toUpperCase() + impact.riskRating.slice(1).toLowerCase()}</span>
                </td>
                <td>{impact.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="section scope-note">
        <h4>Scope Note</h4>
        {reportData.executiveReadinessVerdict.scopeNotes.length > 0 ? (
          <p className='ml-6'>
            {reportData.executiveReadinessVerdict.scopeNotes.join(' ')}
          </p>
        ) : (
          <p>No specific scope notes provided for this report.</p>
        )
        }
      </div>

      <ReportFooter />
    </section>

    <section className="deployment-card report-page">
      <div className="section ">
        <div>
          <div className='grid'>
            <div className="executive-interpretation-box">
              <h3 className="executive-interpretation-title">Executive Interpretation</h3>
              <br />
              <ul className="interpretation">
                {reportData.executiveReadinessVerdict.executiveInterpretation.map((point, index) => (
                  <li key={index}>{point}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      <ReportFooter />
    </section>
    </>
  )
}

const renderMinimumPreconditions = (reportData: RiskPlusReport) => {
  return (
    reportData.leadershipSummary.deploymentRequirements.length > 0 && (
      <div className="ls-preconditions-section">
        <h3 className="ls-col-heading">Minimum Preconditions Before Deployment Proceeds</h3>
        <ol className="ls-precondition-list">
          {reportData.leadershipSummary.deploymentRequirements.map((req: DeploymentRequirement, index) => (
            <li key={index} className="ls-precondition-item">
              <span className="ls-precondition-num">{index + 1}</span>
              <div className="ls-precondition-body">
                <strong className="ls-precondition-label">{req.label}:</strong>
                <ul className="ls-precondition-details">
                  {req.requirement
                    .split(';')
                    .map((s) => s.trim())
                    .filter((s) => s.length > 0)
                    .map((sub, si) => (
                      <li key={si}>{sub}{si < req.requirement.split(';').filter((s) => s.trim().length > 0).length - 1 ? ';' : ''}</li>
                    ))}
                </ul>
              </div>
            </li>
          ))}
        </ol>
      </div>
    )
  )
}
const renderExecutiveSummary = (reportData: RiskPlusReport) => {
  return (
    <section id="toc-executive-summary" className="container executive report-page">
      <h1 className='mb-0'>1. Executive Summary</h1>
      <h4>System Overview</h4>
      <p className=''>{reportData.useCaseOverview.useCaseSummary}</p>

      <div className="icon-list-container">
        <h3>Why Is Risk Mitigation Required</h3>
        <ul className="icon-list">
          {reportData.useCaseOverview.riskDrivers.map((driver, index) => (
            <li key={index}>{driver.label}</li>
          ))}
        </ul>
      </div>
      <div className='mb-4' style={{ fontSize: "clamp(11px, 1.1vw, 14px)" }}>
        These characteristics transform technical failures into institutional risks, amplifying regulatory and reputational exposure.
      </div>

      <h4>Summary of Identified Risks</h4>
      <div className="executive-summary-note">
        <p>
          This report details the risks rated <strong>Critical</strong> and <strong>High</strong> for this deployment.
          Additional risks rated{" "}
          <span className="badge medium" aria-hidden="true" /> <strong>Moderate</strong> and{" "}
          <span className="badge low" aria-hidden="true" /> <strong>Low</strong> have been identified but are not
          included in this report.
        </p>
      </div>
      <div className="ls-risks-grid">
        {reportData.riskRegister
          .filter((risk) => ['high', 'critical'].includes(String(risk.riskRank).toLowerCase()))
          .map((risk) => (
            <div key={risk.id} className="ls-risk-card">
              <div className="ls-risk-card-header">
                <span className={`ls-severity-tag ${risk.riskRank.toLowerCase()}`}>
                  {risk.riskRank.toUpperCase()}
                </span>
                <span className="ls-risk-id-badge">{risk.id}</span>
              </div>
              <h4 className="ls-risk-id">{risk.label}</h4>
              <p className="ls-risk-desc">{risk.riskScenarioDescription}</p>
            </div>
          ))}
      </div>
      {renderMinimumPreconditions(reportData)}
      <ReportFooter />
    </section>
  )
}

const renderHowToReadReport = (useCaseTitle: string) => {

  return (
    <section id="toc-how-to-read" className="report-guide report-page">

      <h1>2. How to Read This Report</h1>
      <p>
        This AIQURIS Risk+ Report provides a rapid inherent risk snapshot for the deployment of
        <strong> {useCaseTitle && useCaseTitle !== "N/A" ? useCaseTitle : "this system's use case"}</strong>. It identifies the top risks specific to this deployment context, explains
        why each risk exists, and outlines the requirements needed to mitigate them. It is intended
        as a planning tool to support informed deployment decisions and readiness for Full
        Assurance.
      </p>
      <p>
        This report does not assess whether mitigation requirements are implemented or
        validate existing controls. It is intended as a planning tool to support informed
        deployment decisions and readiness for Full Assurance.
      </p>

      <div className="inherent-risk-note">
        <p>
          <strong>All risks shown are inherent (pre-mitigation).</strong> They reflect exposure before any
          controls are implemented or verified. They do not represent residual risk and should not
          be read as confirmation that risks are or are not managed.
        </p>
      </div>

      <h3 className="sub-title">Recommended Reading Sequence</h3>
      <ul className="reading-sequence-list">
        <li>
          <strong>Risk Snapshot</strong> (Section 2) provides a one-page summary of the highest exposure
          areas and the key actions required before deployment. This is the fastest way to
          understand whether and under what conditions deployment should proceed.
        </li>
        <li>
          <strong>Executive Summary</strong> (Section 1) provides a narrative overview of the deployment
          context, why the deployment carries its assessed risk level, and the minimum
          preconditions that must be satisfied before go-live.
        </li>
        <li>
          <strong>Risk Domain Profile</strong> (Section 4) shows how risk is distributed across the relevant
          risk domains for this deployment.
        </li>
        <li>
          <strong>Individual risk entries</strong> provide detailed analysis of each identified risk, including the
          hazards that cause it, the conditions that enable it, and the specific requirements
          needed to address it. Each entry is self-contained and can be assigned directly to a
          responsible owner.
        </li>
        <li>
          <strong>Top Requirements summary</strong> provides a consolidated view of all unassessed
          requirements backlog across all risk entries, suitable for use as an input to a remediation plan
          or project backlog.
        </li>
      </ul>

      <h3 className="sub-title">Understanding the Risk Scores</h3>
      <p>
        Risk scores reflect the scale of potential institutional consequences if a risk materialises, not
        the statistical probability of occurrence. They are assessed in the context of the specific
        deployment and its characteristics.
      </p>
      <br />

      <table className="risk-score-table">
        <thead>
          <tr>
            <th>Rating</th>
            <th>Meaning</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><div className="risk-rating-cell"><span className="badge critical"></span> <strong>Critical</strong></div></td>
            <td>Consequences would be severe and potentially system-threatening for the organisation</td>
          </tr>
          <tr>
            <td><div className="risk-rating-cell"><span className="badge high"></span> <strong>High</strong></div></td>
            <td>Significant institutional impact requiring priority attention before deployment</td>
          </tr>
          <tr>
            <td><div className="risk-rating-cell"><span className="badge medium"></span> <strong>Moderate</strong></div></td>
            <td>Meaningful exposure requiring structured mitigation during or shortly after deployment</td>
          </tr>
          <tr>
            <td><div className="risk-rating-cell"><span className="badge low"></span> <strong>Low</strong></div></td>
            <td>Limited exposure manageable through standard operational controls</td>
          </tr>
          <tr>
            <td><div className="risk-rating-cell"><span className="badge negligible"></span> <strong>Negligible</strong></div></td>
            <td>Exposure is present but consequences are minor and unlikely to require dedicated action</td>
          </tr>
          <tr>
            <td><div className="risk-rating-cell"><span className="badge na"></span> <strong>N/A</strong></div></td>
            <td>The risk domain or scenario was assessed and determined not applicable to this deployment</td>
          </tr>
        </tbody>
      </table>

      <h3 className="sub-title">Understanding the Hazard Structure</h3>
      <p>Each risk entry identifies three types of contributing factors:</p>
      <br />
      <ul className="hazard-structure-list">
        <li><strong>Direct Causes</strong> are the technical or operational failures that immediately produce harm.</li>
        <li><strong>Enablers</strong> are the conditions that allow those failures to occur or go undetected.</li>
        <li><strong>Amplifiers</strong> are the deployment characteristics that increase the severity or reach of harm if it materialises.</li>
      </ul>


      {/* <div className="risk-legend">
        <div className="legend-item"><span className="badge critical"></span> Critical</div>
        <div className="legend-item"><span className="badge high"></span> High</div>
        <div className="legend-item"><span className="badge medium"></span> Moderate</div>
        <div className="legend-item"><span className="badge low"></span> Low</div>
        <div className="legend-item"><span className="badge negligible"></span> Negligible</div>
        <div className="legend-item"><span className="badge na"></span> N/A</div>
      </div> */}

      <h3 className="sub-title">What This Report Does Not Provide</h3>
      <p>
        This report does not verify whether any controls or safeguards are in place, nor does it
        assess their effectiveness. Residual risk can only be determined through Full Assurance,
        which involves expert-led validation, evidence review, and formal verification of implemented
        controls. See the final section for the path from this risk snapshot to Full Assurance.
      </p>

      <ReportFooter />
    </section>
  )
}

const renderRiskMethodology = () => {
  return (
    <section id="toc-methodology" className='risk-methodology report-page'>
      <h1>3. Risk Methodology</h1>

      <p>This section describes the analytical framework used to identify, structure, and prioritise the risks presented in this report. Understanding the methodology helps interpret the risk findings and the basis on which they have been assessed.</p>

      <h3>Risk Identification</h3>

      <p>Risks are identified through a harm-first analytical approach. Rather than starting from a catalogue of known AI system failures, the methodology begins with realistic harm scenarios - the actual negative outcomes that could affect users, the organisation, and external stakeholders. Each harm scenario is then traced back to the underlying system hazards that could trigger it and the conditions that would allow it to materialise.</p>

      <p style={{ marginTop: '16px' }}>For each identified risk, three types of contributing factors are analysed:</p>

      <div className="methodology-grid-2">
        <div className="methodology-card-grey">
          <h4>Direct Causes</h4>
          <p>- are the technical or operational failures that could immediately produce harm</p>
        </div>
        <div className="methodology-card-grey">
          <h4>Enablers and Amplifiers</h4>
          <p><strong>Enablers</strong> - are the conditions that allow those failures to occur or go undetected</p>
          <br />
          <p><strong>Amplifiers</strong> - are the deployment characteristics that increase the severity or reach of harm if it materialises</p>
        </div>
      </div>

      <p>This approach ensures that risks are grounded in realistic failure pathways specific to the deployment context, rather than generic model limitations that may or may not be relevant to this use case.</p>

      <h3>Risk Prioritisation</h3>

      <p>Risk ratings reflect the scale of potential institutional consequences if a risk materialises. They are not statistical probabilities of occurrence. Each rating is determined by three factors:</p>

      <ul className="plain-list" style={{ marginTop: '12px', marginBottom: '24px' }}>
        <li><strong>Harm Severity</strong> - the scale of impact on users, the organisation, and external stakeholders if the harm materialises</li>
        <li><strong>Deployment Exposure</strong> - the characteristics of this specific deployment that increase or decrease the likelihood and reach of harm, such as user population, data sensitivity, and system integrations</li>
        <li><strong>Causal Hazard Weighting</strong> - the degree to which identified system characteristics directly enable the harm pathway</li>
      </ul>

      <div className="methodology-flow-grid">
        <div className="methodology-card-outline">
          <h4>System Hazards</h4>
        </div>
        <div className="methodology-arrow">→</div>
        <div className="methodology-card-outline">
          <h4>Stakeholder Harm</h4>
        </div>
        <div className="methodology-arrow">→</div>
        <div className="methodology-card-outline">
          <h4>Deployer Risk</h4>
        </div>
      </div>

      <div className="methodology-limitations">
        <h4>Important Limitations</h4>
        <ul className="plain-list">
          <li>All risks presented in this report are inherent - assessed prior to the implementation or verification of any controls</li>
          <li>Control implementation has not been evaluated</li>
          <li>Control effectiveness has not been validated</li>
          <li>Residual risk can only be determined through a full risk management and assurance process</li>
        </ul>
      </div>
      <ReportFooter />
    </section>
  )
}

const renderDeploymentProfile = (reportData: RiskPlusReport) => {
  return (
    <>
      <section id="toc-deployment-profile" className="container deployment-profile report-page">
        <h1>5. Deployment Risk Profile</h1>
        <div className="grid-2">
          <div>
            <h4>Why Is Risk Mitigation Required</h4>
            <ul>
              {reportData.useCaseOverview.riskDrivers.map((driver, index) => (
                <li key={index}>{driver.description}</li>
              ))}
            </ul>
          </div>
          <div>
            <h4>Deployment Characteristics</h4>
            <ul>
              {reportData.deploymentProfile.deploymentCharacteristics.map((char, index) => (
                <li key={index}>{char.label}</li>
              ))}
            </ul>
          </div>
        </div>
        <div id="toc-risk-concentration" className="risk-concentration-section">
          <h1>6. Risk Concentration</h1>
          <p>The charts below illustrate where inherent risk is concentrated for this deployment, viewed from two complementary perspectives: the type of harm to the deploying organisation, and the external stakeholder groups exposed.</p>
          <h4>Risk Concentration across Harm Categories</h4>
          <p>The chart below illustrates how inherent risk is distributed across the four institutional harm categories for the deploying organisation. The shape and size of the shaded area reflect the relative concentration of exposure across Financial, Legal, Reputational, and Operational dimensions prior to any mitigation.</p>
          <div className="mb-4 flex flex-col items-center">
            <InternalRiskRadarChart reportData={reportData} />
          </div>
          <p>Key contributing risks across harm categories</p>
          <ul className="pl-4 pt-2 pb-2">
            {Object.keys(reportData.deploymentProfile.aiSystemImpact.internal).map((key) => (
              <li key={key}>{reportData.deploymentProfile.aiSystemImpact.internal[key as keyof typeof reportData.deploymentProfile.aiSystemImpact.internal].description}</li>
            ))}
          </ul>

        </div>
        <ReportFooter />
      </section>
      <section className="container deployment-profile report-page">
        <div className="risk-concentration-section">
          <h4>Risk Concentration across External Stakeholder Groups</h4>
          <p>The chart below maps inherent risk exposure across the three external stakeholder groups who may be impacted by this deployment.</p>
          <div className="flex flex-col items-center pb-2">
            <ExternalRiskRadarChart reportData={reportData} />
          </div>

          <p>Key harms by stakeholder group</p>
          <ul className="pl-4 pt-2 pb-2">
            {Object.keys(reportData.deploymentProfile.aiSystemImpact.external).map((key) => (
              <li key={key}>{reportData.deploymentProfile.aiSystemImpact.external[key as keyof typeof reportData.deploymentProfile.aiSystemImpact.external].description}</li>
            ))}
          </ul>

        </div>
        <ReportFooter />
      </section>
    </>
  )
}

const getRiskBadgeDotClass = (name: TRiskRank) => {
  if (name === "critical") return "badge critical";
  if (name === "high") return "badge high";
  if (name === "moderate") return "badge medium";
  if (name === "low") return "badge low";
  if (name === "negligible") return "badge negligible";
  return "badge na";
};
const DETAILED_RISK_FINDINGS_INTRO =
  "This section presents the detailed analysis for each risk identified as Critical or High in this deployment. Each entry describes the risk scenario, the hazards that cause it, the conditions that enable it, the amplifying factors specific to this deployment context, and the required controls needed to address it.";

const renderRiskRegistryItem = (riskDetail: RiskRegistryItem, displayNum: string, prependIntro?: boolean) => {
  const safeId = `toc-risk-${String(riskDetail.id).replace(/[^a-zA-Z0-9-_]/g, "-")}`;
  return (
    <section key={riskDetail.id} id={safeId} className="container risk-detail report-page">
      {prependIntro && (
        <div id="toc-detailed-risk-findings" className="risk-findings-intro">
          <h1>7. Detailed Risk Findings</h1>
          <p>{DETAILED_RISK_FINDINGS_INTRO}</p>
        </div>
      )}
      <div className="flex flex-nowrap justify-between items-start gap-4">
        <h2 className="risk-detail-subheading flex-1 min-w-0">{displayNum} {riskDetail.label} ({riskDetail.riskRank ? riskDetail.riskRank.charAt(0).toUpperCase() + riskDetail.riskRank.slice(1).toLowerCase() : ''})</h2>
        <span className="inline-flex shrink-0 items-center rounded-full bg-slate-300 px-2.5 py-1.5 my-1 text-sm font-medium text-slate-800">
          {riskDetail.id}
        </span>
      </div>
      <span className="severity-indicator">
        <span className={getRiskBadgeDotClass(riskDetail.riskRank)} aria-hidden />
        <span>{riskDetail.riskRank.toUpperCase()} SEVERITY</span>
      </span>
      <h3>{harmCategoryToLabel(riskDetail.harmCategory)}</h3>
      <p className="risk-description">{riskDetail.riskScenarioDescription}</p>

      <div className="risk-grid">
        <div>
          <h3 className="section-title">Key Hazards</h3>
          <div className="hazard-box">
            <h4>Direct Causes</h4>
            <ul>
              {riskDetail.causalHazards.map((hazard, index) => (
                <li key={index}>{hazard.description}</li>
              ))}
            </ul>
          </div>
          <div className="hazard-box">
            <h4>Enablers and Amplifiers</h4>
            <ul>
              {riskDetail.enablingFactors.map((factor, index) => (
                <li key={index}>{factor.description}</li>
              ))}
            </ul>
          </div>
        </div>
        <div>
          <h3>Deployer Impact</h3>
          <ul className="plain-list">
            {riskDetail.deployerImpact.map((impact: { label: string }, index) => (
              <li key={index}>{impact.label || '-'}</li>
            ))}
          </ul>
          <h3>Top Required Controls (Not Verified) </h3>
          <ul className="plain-list">
            {riskDetail.mitigationRequirements.map((req: MitigationRequirement, index) => (
              <li key={index}>{req.description || req.label || '-'}</li>
            ))}
          </ul>
        </div>
      </div>
      <ReportFooter />
    </section>
  )
}

const renderRiskRegistry = (reportData: RiskPlusReport) => {
  return (
    <>
      {reportData.riskRegister.map((riskDetail: RiskRegistryItem, index) =>
        renderRiskRegistryItem(riskDetail, `7.${index + 1}`, index === 0)
      )}
    </>
  );
}

const renderTopRequirements = (reportData: RiskPlusReport) => {
  return (
    <section id="toc-top-requirements" className="container top-requirements report-page report-page">
      <h1>8. Top Required Controls (Not Verified) </h1>
      <p>
        {reportData.deploymentRequirements.deploymentRequirementSummary}
      </p>
      <div className="risk-detail-grid">
        {reportData.riskRegister.map((riskDetail) => (
          <div key={riskDetail.id} className="risk-detail">
            <h4>{riskDetail.id} - {riskDetail.label} ({riskDetail.riskRank})</h4>
            <ul className="plain-list">
              {riskDetail.mitigationRequirements.map((req: { label: string }, index) => (
                <li key={index}>{req.label || ''}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className='important-notes'>
        <div className="notes-header">
          <span className="notes-icon">📝</span>
          <h3>Important Notes</h3>
        </div>
        <ul>
          {reportData.deploymentRequirements.disclaimerNotes.map((note, index) => (
            <li key={index}>{note}</li>
          ))}
        </ul>
      </div>
      <ReportFooter />
    </section>
  )
}

const renderAssurancePage = (_reportData: RiskPlusReport) => {
  return (
    <section id="toc-assurance" className="assurance report-page">
      <h1>9. From Risk Assessment to Risk Control and Assurance</h1>

      <p>
        Assessing risk is the critical first step in deploying AI systems that are safe, reliable, and compliant. Without a clear understanding of where risk is concentrated, what is driving it, and what controls are required, neither effective risk management nor meaningful assurance can be achieved. This report establishes that foundation.
      </p>

      <p>
        Risk assessment alone, however, is not sufficient. Knowing what controls are needed is distinct from having the detailed guidance to implement them, operating them effectively, and verifying that they are working as intended. Achieving safe, reliable, and compliant AI deployment requires all three stages to be completed in sequence: assess, manage, and assure.
      </p>

      <div className="assurance-tabs">
        <div className="assurance-tab active">Assess</div>
        <div className="assurance-tab">Control</div>
        <div className="assurance-tab">Assure</div>
      </div>

      <p>
        Building directly on the findings of this report, the risk management and assurance stage involves:
      </p>

      <ul className="assurance-list">
        <li>Developing detailed control specifications that enable the organisation to implement the required controls correctly across all relevant system components</li>
        <li>Operating and monitoring those controls on an ongoing basis to ensure continued effectiveness</li>
        <li>Verifying through evidence review that implemented controls are achieving their intended risk reduction</li>
        <li>Calculating residual risk and producing a formal assurance report suitable for governance, regulatory, and audit purposes</li>
      </ul>

      <p>
        The stronger and more precise the risk assessment foundation, the more targeted and effective the subsequent management and assurance work will be. The risks, drivers, and required controls identified in this report form the direct input to that process.
      </p>

      <p>
        Until risk management and assurance is completed, deployment decisions should be made with the inherent risk profile established in this report explicitly acknowledged by accountable stakeholders.
      </p>
      <ReportFooter />
    </section>
  )
}


const renderRiskPillarCard = (pillar: RiskProfilePillar, key?: string | number) => {

  const totalCharacteristics: number = Object.values(pillar.characteristicsHistogram).reduce((acc, val) => acc + val, 0) || 1;

  const barPercentage: number[] = [];
  for (const [risk, count] of Object.entries(pillar.characteristicsHistogram)) {
    barPercentage[toRankIdx(risk as TRiskRankWithNA)] = Math.round((count / totalCharacteristics) * 1000) / 10
  }

  return (

    <div key={key} className="pillar-card">
      <div className="pillar-header">
        <h5 className="pillar-title">{pillar.label}</h5>
        <span className={`legend-color risk0${toRankIdx(pillar.riskRank)}`}>&nbsp;</span>
      </div>
      <div className="risk_bar">

        {barPercentage.length > 0 ?
          [5, 4, 3, 2, 1, 0].map((idx) => {
            const c = barPercentage[idx];
            if (c === undefined) return null;
            return (
              <div key={idx} className={`risk0${idx}`} style={{ width: `${c}%` }}>&nbsp; </div>
            );
          }) : (
            <div className="risk00" style={{ width: '100%' }}>&nbsp; </div>
          )
        }
      </div>
      <strong style={{ marginBottom: 16 }}>{pillar.histogramComment}</strong>
      <p>{pillar.executiveComment}</p>
    </div>
  )
}


const renderRiskProfile = (reportData: RiskPlusReport) => {
  return (
    <section id="toc-risk-domain-profile" className="container top-requirements report-page report-page">
      <h1>4. Risk Domain Profile</h1>
      <div className="pillars-grid">
        {reportData.riskProfileSummary.pillars.map((pillar, idx) => renderRiskPillarCard(pillar, pillar.label || `pillar-${idx}`))}
      </div>
      {reportData.riskProfileSummary.pillars.map((pillar, idx) =>
        <div key={pillar.label || `pillar-${idx}`} className="pb-2 risk-profile-summary-item text-justify">
          <strong>{pillar.label}</strong>
          <p> {pillar.summary} </p>
        </div>
      )}
      <ReportFooter />
    </section>
  )
}

export const RenderRiskPlusReport = forwardRef<HTMLDivElement, { reportData: RiskPlusReport }>(
  function RenderRiskPlusReport({ reportData }, ref): JSX.Element {
    useReportPagination("risk-report-content");
    useTocPageNumbers("risk-report-content");

    return (
      <div id="risk-report-content" ref={ref}>
        <div className="report-content-inner">

          {renderTitlePage(reportData)}

          {renderDeploymentVerdict(reportData)}

          {renderTableOfContents(reportData)}

          {renderExecutiveSummary(reportData)}

          {renderHowToReadReport(reportData.titlePage.useCaseTitle || "")}

          {renderRiskMethodology()}

          {renderRiskProfile(reportData)}

          {renderDeploymentProfile(reportData)}

          {renderRiskRegistry(reportData)}

          {renderTopRequirements(reportData)}

          {renderAssurancePage(reportData)}

          {renderDisclaimer()}

        </div>
        <div className="report-footer-fixed" aria-hidden="true">
          <ReportFooter />
        </div>
      </div>
    );
  }
);